import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import { GrowerForm } from './-grower-form'
import type { Grower } from '@/lib/api/growers-api'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useCreateGrower,
    useDeleteGrower,
    useGrowers,
    useUpdateGrower,
} from '@/hooks/use-growers-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agriculture/growers',
)({
    component: GrowersPage,
})

function GrowersPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('growers.write')
    const [editing, setEditing] = useState<Grower | null>(null)
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')

    const { data } = useGrowers({ per_page: 100, search: search || undefined })
    const rows = data?.data ?? []

    const createMut = useCreateGrower()
    const updateMut = useUpdateGrower(editing?.id ?? '')
    const deleteMut = useDeleteGrower()

    return (
        <div className="space-y-4 p-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">{m.grower_title()}</h1>
                {canWrite && (
                    <Button
                        size="sm"
                        onClick={() => {
                            setEditing(null)
                            setOpen(true)
                        }}
                    >
                        <Plus className="size-4" /> {m.grower_new()}
                    </Button>
                )}
            </div>

            <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={m.grower_search_placeholder()}
                className="max-w-xs"
                data-testid="grower-search"
            />

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.grower_name()}</TableHead>
                        <TableHead>{m.grower_phone()}</TableHead>
                        <TableHead>{m.grower_address()}</TableHead>
                        <TableHead>
                            {m.grower_is_member_cooperative()}
                        </TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell className="font-medium">
                                {row.name}
                            </TableCell>
                            <TableCell>{row.phone ?? '—'}</TableCell>
                            <TableCell>{row.address ?? '—'}</TableCell>
                            <TableCell>
                                {row.is_member_cooperative
                                    ? m.grower_member_yes()
                                    : m.grower_member_no()}
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                                {canWrite && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => {
                                                setEditing(row)
                                                setOpen(true)
                                            }}
                                        >
                                            <Pencil className="size-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => {
                                                if (
                                                    window.confirm(
                                                        m.grower_delete_confirm(),
                                                    )
                                                ) {
                                                    deleteMut.mutate(row.id)
                                                }
                                            }}
                                        >
                                            <Trash2 className="size-4" />
                                        </Button>
                                    </>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {editing ? m.grower_edit() : m.grower_new()}
                        </DialogTitle>
                    </DialogHeader>
                    <GrowerForm
                        initial={editing}
                        onSubmit={async (values) => {
                            try {
                                if (editing) {
                                    await updateMut.mutateAsync(values)
                                } else {
                                    await createMut.mutateAsync(values)
                                }
                                setOpen(false)
                            } catch {
                                // onError handler in the mutation hook surfaces the toast
                            }
                        }}
                        submitting={createMut.isPending || updateMut.isPending}
                    />
                </DialogContent>
            </Dialog>
        </div>
    )
}
